import { SizeType } from "../types";
import parseIconString, { CustomIconStringReturnElementsType, IconStringReturnType, isValidIconString } from "components/src/utils/parseIconString";
import brandIconsWithModes from "tokens/build/js/brandIconsWithModes";

const folderMap = {
  brand_: {
    folderName: "brand-icons",
    regex: "^brand_",
    includes: "brand_"
  },
  ui_: {
    folderName: "ui-icons",
    regex: "_?ui_",
    includes: "_ui_"
  },
  flag_: {
    folderName: "country-flags",
    regex: "^flag_",
    includes: "flag_",
    excludes: ["_sports_flag_"]
  },
  _sports_: {
    folderName: "sports-icons",
    regex: "_?sports_(?!flag)",
    includes: "_sports_",
    excludes: ["_sports_flag_"]
  },
  _sports_flag_: {
    folderName: "sports-flag-icons",
    regex: "_sports_flag_",
    includes: "_sports_flag_"
  },
  __helmet_: {
    folderName: "team-logos-alt",
    regex: "__helmet",
    includes: "__helmet"
  },
  logo_: {
    folderName: "team-logos",
    regex: "^logo_.+(?!helmet)",
    includes: "logo_",
    excludes: ["__helmet"]
  }
};

const bitmapFolders = ["sports-icons", "sports-flag-icons", "team-logos-alt"];

const sizeToBitmapSizeMap = {
  "2x": "sm",
  xs: "sm",
  sm: "md",
  md: "md",
  lg: "md",
  xl: "md",
  "2xl": "md",
  "3xl": "lg",
  "4xl": "lg",
  "5xl": "lg",
};

function getBitmapSizeForFolder(size, folder) {
  const bitmapSize = bitmapSizeTiers.includes(size) ? size : getBitmapSize(size);
  if (folder === 'sports-backgrounds' && bitmapSize === 'md') return 'lg';
  return bitmapSize;
}

function getBitmapSize(size) {
  if (typeof size === "string" && size in sizeToBitmapSizeMap)
    return sizeToBitmapSizeMap[size];
  if (typeof size === "number") {
    if (size <= 12) return "sm";
    if (size > 12 && size <= 32) return "md";
    if (size > 32) return "lg";
  }
  return "md";
}

const identifiers = Object.keys(folderMap);

export function getCuiFolder(name) {
  const [id = ""] = identifiers.filter((id) => {
    let excludes = false;
    if (Array.isArray(folderMap[id].excludes)) {
      excludes = folderMap[id].excludes.some(exclude => !name?.includes(exclude)); 
    } else {
      excludes = !name?.includes(folderMap[id].excludes);
    }
    return name?.includes(folderMap[id].includes) && excludes;
    // console.log("REGEX", {name, id, fmap: folderMap[id].regex, regexResult: new RegExp(folderMap[id].regex).test(name) });
    // console.log({name, id, fmap: folderMap[id].includes, fmapExclude: folderMap[id]?.excludes, result: name.includes(folderMap[id].includes) && excludes });
    // return new RegExp(folderMap[id].regex).test(name);
  });
  return folderMap[id]?.folderName;
}

export function isValidIcon(icon) {
  if (!icon) return false;
  return identifiers.some((prefix) => icon.includes(prefix));
}

function prependStr(str, prependStr = "icon" ) {
  return /^(ui_|sports_)/.test(str)
    ? `${prependStr}_${str}`
    : str;
}

function replaceDashes(str) {
  return typeof str === "string" && str?.includes("-")
  ? str?.replace(/-/g, "_")
  : str;
}

const brandIconsWithModesMap = brandIconsWithModes.reduce((acc, icon) => {
  acc[icon] = { light: `${icon}_onLight`, dark: `${icon}_onDark` };
  return acc;
}, {});

const httpsRE = /^https?/;

export function useImageResolver(
  name,
  imageDir = "/public",
  size: SizeType = "md",
  mode = "light",
  exact = false
) {
  if (exact) {
    const folder = name?.split('/')[0];
    const fileName = name?.split('/').pop()?.split('.')[0];
    const ext = name?.split('.').pop();
    const bitmapSize = getBitmapSizeForFolder(size, folder);
    const src = `${imageDir}/${folder}/${fileName}__${bitmapSize}.${ext}`;
    const imageSet = buildImageSet(fileName, ext, imageDir, folder, size);
    return { src, imageSet, canColor: false, folder, iconName: name };
  }
  
  let _name = name;

  if (name && isValidIconString(name)) {
    const { name: parsedName  } = parseIconString(name) as CustomIconStringReturnElementsType;
    _name = prependStr(replaceDashes(parsedName));
  } else {
    _name = prependStr(replaceDashes(name));
  };
  
  _name =  _name && _name in brandIconsWithModesMap
  ? brandIconsWithModesMap[_name][mode]
  : _name;

  // Special check for NFC helmets, patch: TODO - add helmet filenames to teams.json
  if (_name?.includes("nfc sb") || _name?.includes("afc sb")) _name = _name?.replace(" sb", "");
  
  const folder = getCuiFolder(_name);
  const isBitmapType = bitmapFolders.includes(folder);

  const isFlagOrLogo = _name?.includes("flag_") || _name?.includes("logo_");
  
  const isAlternateLogo = isFlagOrLogo && _name?.includes("__helmet");

  const isBrandColor =
    _name?.includes("brand_") &&
    (_name?.includes("_color") ||
      _name?.includes("_onLight") ||
      _name?.includes("_onDark"));
  const canColor =
    !isFlagOrLogo &&
    !isBrandColor &&
    !_name?.includes("_color") &&
    !isBitmapType;

  // console.log({folder, _name})

  if (httpsRE.test(name)) return { src: name, canColor: false, folder: undefined, iconName: undefined };
  const src = `${imageDir}/${folder}/${withFileType(_name, size, isBitmapType)}`;
  const srcSet = isBitmapType ? buildSrcSet(_name, imageDir, folder, size) : undefined;
  return { src, srcSet, folder, canColor, iconName: _name };
}

const bitmapSizeTiers = ["sm", "md", "lg"];

function buildSrcSet(name, imageDir, folder, size) {
  const floor = getBitmapSize(size);
  const floorIndex = bitmapSizeTiers.indexOf(floor);
  const remaining = bitmapSizeTiers.slice(floorIndex);
  if (remaining.length <= 1) return undefined;
  return remaining.map((tier, i) => `${imageDir}/${folder}/${name}__${tier}.png ${i + 1}x`).join(", ");
}

const folderTiers = {
  "sports-backgrounds": ["sm", "lg"],
};

function buildImageSet(fileName, ext, imageDir, folder, size) {
  const tiers = folderTiers[folder] || bitmapSizeTiers;
  const floor = getBitmapSize(size);
  const floorIndex = tiers.indexOf(floor);
  const remaining = floorIndex >= 0 ? tiers.slice(floorIndex) : tiers;
  if (remaining.length <= 1) return undefined;
  const entries = remaining.map((tier, i) => `url("${imageDir}/${folder}/${fileName}__${tier}.${ext}") ${i + 1}x`).join(", ");
  return `image-set(${entries})`;
}

function withFileType(name, size, isBitmapType) {
  if (name && isBitmapType) {
    return `${name}__${getBitmapSize(size)}.png`
  } else {
    return name ? `${name}.svg` : name;
  }
}